import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { CompanySearchHitRecord, Country } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { ApiErrorMessage, NoddiInput, NoddiSpinner } from 'noddi-ui-web';
import { useDebounceValue } from 'noddi-util';
import { useId, useState } from 'react';

type SelectedCompany = {
  name: string;
  organizationNumber: string;
};

type CompanyRegistrySearchProps = {
  registryCountry: Country;
  errorCodes: Record<string, string> & { genericErrorMessage: string };
  onSelect: (company: SelectedCompany) => void;
  onClear: () => void;
  onSwitchToManual: () => void;
};

const ORG_NUMBER_LENGTH = 9;
const MIN_SEARCH_LENGTH = 3;

const digitsOf = (value: string) => value.replace(/\D/g, '');

/**
 * Company search against the backend company registry during signup: one
 * input that resolves a 9-digit organization number or suggests companies
 * by name, with the hits in an in-flow list so the phone keyboard never
 * covers them (same reasoning as AddressSearchInline). Picking a hit or
 * pressing Enter confirms the company; the manual-entry link below keeps
 * signup possible when the registry has nothing.
 */
export const CompanyRegistrySearch = ({
  registryCountry,
  errorCodes,
  onSelect,
  onClear,
  onSwitchToManual
}: CompanyRegistrySearchProps) => {
  const listboxId = useId();
  const [query, setQuery] = useState('');
  const [isBrowsing, setIsBrowsing] = useState(false);

  const debouncedQuery = useDebounceValue({ value: isBrowsing ? query : '', delay: 300 });
  const trimmedQuery = debouncedQuery.trim();
  const digits = digitsOf(trimmedQuery);
  const isOrgNumberQuery = digits.length === ORG_NUMBER_LENGTH;
  const isSearchableName = !isOrgNumberQuery && trimmedQuery.length >= MIN_SEARCH_LENGTH;
  const isListOpen = isBrowsing && trimmedQuery.length > 0;

  const {
    data: lookupData,
    error: lookupError,
    isFetching: isLookupFetching
  } = noddiAsync.useGet({
    type: URLKeys.getOrganizationSignupCompanyLookup,
    input: { organizationNumber: parseInt(digits, 10), country: registryCountry },
    queryConfig: { enabled: isBrowsing && isOrgNumberQuery }
  });

  const {
    data: searchData,
    error: searchError,
    isFetching: isSearchFetching
  } = noddiAsync.useGet({
    type: URLKeys.getOrganizationSignupCompanySearch,
    input: { name: trimmedQuery, country: registryCountry },
    queryConfig: { enabled: isBrowsing && isSearchableName }
  });

  // An organization-number lookup is a single deterministic hit, so it joins
  // the name-search hits as the one suggestion to confirm.
  const lookupHit: CompanySearchHitRecord | null = lookupData
    ? { name: lookupData.name, organizationNumber: String(lookupData.organizationNumber), organizationForm: null }
    : null;
  const searchHits: CompanySearchHitRecord[] = searchData ?? [];
  const suggestions: CompanySearchHitRecord[] = lookupHit ? [lookupHit] : searchHits;
  const isSearching = isLookupFetching || isSearchFetching || query.trim() !== trimmedQuery;

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setIsBrowsing(true);
    // Typing invalidates whatever was picked before — drop it rather than
    // submitting a company the customer has disowned.
    onClear();
  };

  const handleClear = () => {
    setQuery('');
    setIsBrowsing(false);
    onClear();
  };

  const handleSelect = (hit: CompanySearchHitRecord) => {
    setQuery(hit.name);
    setIsBrowsing(false);
    onSelect({ name: hit.name, organizationNumber: hit.organizationNumber });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsBrowsing(false);
      return;
    }
    if (event.key === 'Enter' && isListOpen && suggestions.length > 0) {
      event.preventDefault();
      const first = suggestions[0];
      if (first) {
        handleSelect(first);
      }
    }
  };

  return (
    <div className='flex flex-col gap-1'>
      <NoddiInput
        label={t`Organization number or company name`}
        role='combobox'
        aria-expanded={isListOpen}
        aria-controls={listboxId}
        aria-autocomplete='list'
        autoComplete='off'
        placeholder={t`e.g. 123456789 or Acme AS`}
        value={query}
        startAdornment={<NoddiIcon name='Building2' className='text-muted-foreground' />}
        endAdornment={
          isSearching ? (
            <NoddiSpinner size='small' />
          ) : query.length > 0 ? (
            <button
              type='button'
              onClick={handleClear}
              aria-label={t`Clear`}
              className='flex items-center text-muted-foreground hover:text-foreground'
            >
              <NoddiIcon name='X' />
            </button>
          ) : undefined
        }
        onChange={(event) => handleQueryChange(event.target.value)}
        onFocus={() => setIsBrowsing(true)}
        onKeyDown={handleKeyDown}
      />

      {lookupError && <ApiErrorMessage error={lookupError} errorCodes={errorCodes} />}
      {searchError && <ApiErrorMessage error={searchError} errorCodes={errorCodes} />}

      {isListOpen && (
        <div
          id={listboxId}
          role='listbox'
          aria-label={t`Organization number or company name`}
          className='max-h-64 overflow-y-auto rounded-md border border-input bg-white shadow-xs'
        >
          {suggestions.map((hit) => (
            <button
              key={hit.organizationNumber}
              type='button'
              role='option'
              aria-selected={false}
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(hit)}
              className='block w-full border-b border-border p-3 text-left last:border-b-0 hover:bg-accent'
            >
              <span className='block text-sm font-medium'>{hit.name}</span>
              <span className='block text-sm text-muted-foreground'>
                <Trans>Org number</Trans>: {hit.organizationNumber}
              </span>
            </button>
          ))}

          {suggestions.length === 0 &&
            (isSearching ? (
              <div className='flex justify-center py-4'>
                <NoddiSpinner size='small' />
              </div>
            ) : (
              isSearchableName && (
                <div className='px-3 py-4 text-sm text-muted-foreground'>
                  <Trans>No matching companies</Trans>
                </div>
              )
            ))}
        </div>
      )}

      <button
        type='button'
        onClick={onSwitchToManual}
        className='self-start text-sm text-muted-foreground underline hover:text-foreground'
      >
        <Trans>Enter company details manually instead</Trans>
      </button>
    </div>
  );
};
